import { __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { debounce } from 'throttle-debounce';
import { classNames, analyticsTracker, liveAnnounce, Level } from 'uitk-react-utils';
import styles from './uitk-carousel.css';
import { UitkHeadingTags } from './types.esm.js';
import { UitkCarouselItemList } from './uitk-carousel-item-list.esm.js';
import { UitkCarouselNav } from './uitk-carousel-nav.esm.js';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
var getClassNameList = classNames.bind(styles);
var UitkCarouselLocalAnalyticsIdType;
(function (UitkCarouselLocalAnalyticsIdType) {
    UitkCarouselLocalAnalyticsIdType["clickLeft"] = "clickLeft";
    UitkCarouselLocalAnalyticsIdType["clickRight"] = "clickRight";
    UitkCarouselLocalAnalyticsIdType["scrollLeft"] = "scrollLeft";
    UitkCarouselLocalAnalyticsIdType["scrollRight"] = "scrollRight";
})(UitkCarouselLocalAnalyticsIdType || (UitkCarouselLocalAnalyticsIdType = {}));
var Carousel = function (props) {
    var analytics = props.analytics, buttonText = props.buttonText, carouselHeaderLevel = props.carouselHeaderLevel, carouselHeaderText = props.carouselHeaderText, carouselHeaderVisible = props.carouselHeaderVisible, carouselName = props.carouselName, carouselRef = props.carouselRef, children = props.children, className = props.className, domRef = props.domRef, fullSheetVisible = props.fullSheetVisible, hideNav = props.hideNav, itemsVisible = props.itemsVisible, itemsMaxHeight = props.itemsMaxHeight, l10n = props.l10n, loop = props.loop, navigationIconId = props.navigationIconId, onChange = props.onChange, onIndexChange = props.onIndexChange, pageBy = props.pageBy, pagingInset = props.pagingInset, pagingOverlay = props.pagingOverlay, peek = props.peek, shouldSnapOnScroll = props.shouldSnapOnScroll, startIndex = props.startIndex, stretchToFill = props.stretchToFill, wrapItems = props.wrapItems;
    var _a = React.useState(false), mounted = _a[0], setMounted = _a[1];
    var _b = React.useState(0), currentIndex = _b[0], setCurrentIndex = _b[1];
    var _c = React.useState(false), isTouch = _c[0], setIsTouch = _c[1];
    var _d = React.useState(false), isRtl = _d[0], setIsRtl = _d[1];
    var _e = React.useState('sm'), size = _e[0], setSize = _e[1];
    var _f = React.useState(false), sizeInitialized = _f[0], setSizeInitialized = _f[1];
    var NAVIGATE_WITH_BTN = React.useRef(false); // Scrolling using a paging button
    var AVOID_FREE_SCROLL = React.useRef(false); // Free scrolling is scrolling the carousel without using the paging buttons
    var SCROLL_WITHOUT_ANIMATION = React.useRef(false);
    var WRAPPER = React.useRef(null);
    // Default style variables
    var peekWidth = 48;
    var itemPadding = 12;
    // Interaction Timer of resize handler
    var INTERACTION_TIMER = 200;
    var SCROLL_TIMER = 350;
    var itemCount = React.Children.count(children);
    var wrapperRef = domRef !== null && domRef !== void 0 ? domRef : WRAPPER;
    var containerRef = React.useRef(null);
    var carouselListRef = React.useRef(null);
    // This can be non-nullable, since setViewportSize will always set a valid size
    var currentItemsVisible = itemsVisible[size];
    // Below Values are reffed because we want them to be maintained between renders
    var viewed = React.useRef([]);
    // Scroll distance is the distance between the start of one card and the next.
    var scrollDistance = React.useRef(0);
    // Distance from origin of the left-most item (or right-most item for RTL)
    var positionWithinCarousel = React.useRef(0);
    // We use this to know if the carousel is going forwards or backwards
    var directionOfScrollIsForward = React.useRef(true);
    // A11y related functions -- Starts
    var announceCards = function () {
        if (containerRef.current) {
            var visibleCardHeadingsElements = containerRef.current.querySelectorAll("\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h1:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h2:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h3:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h4:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h5:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h6:not([aria-hidden=\"true\"])\n        ");
            var visibleCardHeadings_1 = Array.from(visibleCardHeadingsElements).map(function (heading) { return heading.innerText || heading.textContent; });
            if (visibleCardHeadings_1.length > 0) {
                setTimeout(function () {
                    liveAnnounce(l10n.formatText('carousel.cardChange', visibleCardHeadings_1.join(', ')), Level.POLITE);
                }, 200);
            }
            else {
                setTimeout(function () {
                    liveAnnounce(l10n.formatText('gallerycarousel.imageChange', currentIndex + 1, itemCount, ''), Level.POLITE);
                }, 200);
            }
        }
    };
    var setTabIndex = function () {
        if (containerRef.current) {
            var carouselItems = containerRef.current.querySelectorAll('.uitk-carousel-item');
            var carouselItemsArray = Array.from(carouselItems);
            carouselItemsArray.forEach(function (item) {
                var carouselElement = item.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
                var carouselElementArray = Array.from(carouselElement);
                carouselElementArray
                    .filter(function (elem) { return !elem.hasAttribute('aria-hidden'); })
                    .forEach(function (elem) {
                    return elem[item.hasAttribute('aria-hidden') ? 'setAttribute' : 'removeAttribute']('tabindex', '-1');
                });
            });
        }
    };
    // A11y related functions -- Ends
    // Analytics functions -- Starts
    var viewItemAnalytics = function () {
        var vis = currentItemsVisible;
        if (peek) {
            vis += 1;
        }
        for (var i = 0; i < vis && currentIndex + i < React.Children.count(children); ++i) {
            if (!viewed.current[currentIndex + i]) {
                viewed.current[currentIndex + i] = true;
                if (typeof props.itemViewedCallback === 'function') {
                    props.itemViewedCallback(currentIndex + i);
                }
                analytics && analyticsTracker(analytics, "".concat(carouselName, ".Carousel.itemViewed.").concat(currentIndex + i), '');
            }
        }
    };
    var scrollAnalytics = React.useCallback(function (scrollRight) {
        analytics &&
            analyticsTracker(analytics, "".concat(carouselName, ".Carousel.").concat(scrollRight ? UitkCarouselLocalAnalyticsIdType.scrollRight : UitkCarouselLocalAnalyticsIdType.scrollLeft), '');
    }, [analytics, carouselName]);
    // Analytics functions -- Ends
    // Carousel main functions -- Starts
    var getCurrentIndex = function () {
        if (containerRef.current) {
            var containerElement = containerRef.current;
            var scrollLeft = containerElement.scrollLeft, scrollWidth = containerElement.scrollWidth;
            var carouselWidth = containerElement.getBoundingClientRect().width;
            var childWidth = scrollDistance.current;
            var distanceFromOrigin = Math.abs(scrollLeft);
            // Edge case for last item of carousel in view along with peeking last element of previous frame.
            // When scroll reaches the end of container then scrollLeft should be equal to scrollWidth - carouselWidth
            // but as subpixels are rounded to nearest integer this calc fails so added -5 to overcome it
            // Edge case only applies when there is more than one item
            var edgeCase = itemCount > 1 && distanceFromOrigin > Math.round(scrollWidth - carouselWidth - 5) ? 1 : 0;
            return Math.floor(distanceFromOrigin / childWidth) + edgeCase;
        }
        return 0;
    };
    var incrementIndexAsNeeded = function (index) {
        /* istanbul ignore else */
        if (directionOfScrollIsForward.current === true) {
            return index + 1;
        }
        return index;
    };
    var getIndexToSnapTo = function (index) {
        var maxIndex = itemCount - currentItemsVisible;
        var newIndex = incrementIndexAsNeeded(index);
        if (newIndex > maxIndex) {
            return maxIndex;
        }
        return newIndex;
    };
    var setScrollingForwardRef = function (position) {
        if (position > positionWithinCarousel.current) {
            directionOfScrollIsForward.current = true;
        }
        else {
            directionOfScrollIsForward.current = false;
        }
    };
    /* istanbul ignore next */
    var getIncrementalPosition = function (time, positionToSnapTo, positionUserScrolledTo, range) {
        // Quadratic easein-easeout function // refer to https://easings.net/#easeInOutQuad
        var curve = function (x) { return (x < 0.5 ? 2 * Math.pow(x, 2) : 1 - Math.pow((-2 * x + 2), 2) / 2); };
        var done = time >= SCROLL_TIMER;
        var position = done ? positionToSnapTo : positionUserScrolledTo + range * curve(time / SCROLL_TIMER);
        return { done: done, position: position };
    };
    var reverseRtlValue = function (value) { return (isRtl ? value * -1 : value); }; // The scrollLeft method returns a negative value in RTL but our functions frequently expect a positive (or reverse) value
    // Scroll carousel to newPosition pixels from origin (which is the left edge for LTR and the right edge for RTL)
    var scrollCarousel = function (newPosition) {
        var startTimestamp;
        if (containerRef.current) {
            var containerElement_1 = containerRef.current;
            var positionUserScrolledTo_1 = reverseRtlValue(containerElement_1.scrollLeft);
            var range_1 = newPosition - positionUserScrolledTo_1;
            // Incrementally advance the carousel using the curve value described above
            // istanbul ignore next
            var step_1 = function (timestamp) {
                if (!startTimestamp) {
                    startTimestamp = timestamp;
                }
                var _a = getIncrementalPosition(timestamp - startTimestamp, newPosition, positionUserScrolledTo_1, range_1), done = _a.done, position = _a.position;
                containerElement_1.scrollLeft = reverseRtlValue(position); // This moves the carousel
                if (!done) {
                    window.requestAnimationFrame(step_1);
                }
            };
            if (SCROLL_WITHOUT_ANIMATION.current) {
                // Occurs when startIndex is set
                SCROLL_WITHOUT_ANIMATION.current = false;
                containerElement_1.scrollLeft = reverseRtlValue(newPosition);
                setMounted(true);
            }
            else {
                window.requestAnimationFrame(step_1);
            }
            scrollAnalytics(range_1 > 0);
            positionWithinCarousel.current = newPosition;
        }
    };
    var scrollCarouselWithNoSnap = function () {
        if (containerRef.current) {
            var positionUserScrolledTo = containerRef.current.scrollLeft;
            var isRightScroll = positionUserScrolledTo - positionWithinCarousel.current > 0;
            scrollAnalytics(isRightScroll);
            positionWithinCarousel.current = positionUserScrolledTo;
        }
    };
    // Adding this ignore comment to prevent this issue https://jira.expedia.biz/browse/EGDS-3032
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var onScroll = React.useCallback(debounce(INTERACTION_TIMER, function () {
        if (AVOID_FREE_SCROLL.current) {
            AVOID_FREE_SCROLL.current = false;
            return false;
        }
        if (containerRef.current) {
            var carouselElement = containerRef.current;
            var positionUserScrolledTo = reverseRtlValue(carouselElement.scrollLeft); // Distance between origin and the position the user scrolled to
            // Cancel snap to scroll if scroll difference is off by just a rounding error, or if it is a tab event
            if (Math.abs(positionUserScrolledTo - positionWithinCarousel.current) < 1 || NAVIGATE_WITH_BTN.current) {
                return;
            }
            setScrollingForwardRef(positionUserScrolledTo);
            var childWidth = scrollDistance.current;
            var snapToIndex = getIndexToSnapTo(getCurrentIndex());
            var positionToSnapTo = childWidth * snapToIndex; // Distance between origin and the new position to snap to
            var range = positionToSnapTo - positionUserScrolledTo;
            var maxScrollLeft = carouselElement.scrollWidth - carouselElement.clientWidth;
            // Because this function is attached to the onScroll property of the component and calls a function which scrolls the carousel,
            // the onScroll function will always run twice, which is why we need to prevent it from scrolling a second time.
            if (range === 0 && positionUserScrolledTo > 0 && positionUserScrolledTo < maxScrollLeft) {
                return;
            }
            if (shouldSnapOnScroll) {
                var effectivePeekWidth = peek && snapToIndex !== 0 ? (peekWidth + itemPadding) / 2 : 0;
                scrollCarousel(positionToSnapTo - effectivePeekWidth);
            }
            else {
                scrollCarouselWithNoSnap();
            }
            setCurrentIndex(snapToIndex);
        }
    }), [size, isRtl]);
    // Prevents tab events from activating scrolling
    var onKeyboardEvent = React.useCallback(function (e) {
        if (e.key) {
            NAVIGATE_WITH_BTN.current = true;
            AVOID_FREE_SCROLL.current = false;
        }
    }, [size, isRtl]);
    React.useEffect(function () {
        document.body.addEventListener('keydown', onKeyboardEvent);
        return function () {
            document.body.removeEventListener('keydown', onKeyboardEvent);
        };
    }, [onKeyboardEvent]);
    var setScrollDistance = function () {
        if (containerRef.current) {
            var carouselWidth = containerRef.current.getBoundingClientRect().width;
            var effectivePeekWidth = peek ? itemPadding + peekWidth : 0;
            scrollDistance.current =
                (carouselWidth - (currentItemsVisible - 1) * itemPadding - effectivePeekWidth) / currentItemsVisible +
                    itemPadding;
        }
    };
    var setViewportSize = function () {
        var carouselListElement = carouselListRef === null || carouselListRef === void 0 ? void 0 : carouselListRef.current;
        if (carouselListElement) {
            setScrollDistance();
            var carouselSize = getComputedStyle(carouselListElement).getPropertyValue('--size') || 'sm';
            if (carouselSize === 'xl' && !itemsVisible.xl) {
                carouselSize = 'lg'; // Backward compatibility if xl is not passed
            }
            setSize(carouselSize);
            if (!sizeInitialized)
                setSizeInitialized(true);
        }
    };
    var page = function (direction) {
        var capPageBy = pageBy > currentItemsVisible ? currentItemsVisible : pageBy;
        var index = direction === 1 ? currentIndex + Number(capPageBy) : currentIndex - Number(capPageBy);
        var limit = itemCount - currentItemsVisible;
        if (index < 0) {
            index = loop ? itemCount - 1 : 0;
        }
        else if (index === limit) {
            index = limit;
        }
        else if (index > limit) {
            index = loop ? 0 : limit;
        }
        NAVIGATE_WITH_BTN.current = true;
        setCurrentIndex(index);
    };
    var onPrevCb = function () {
        page(-1);
    };
    var onNextCb = function () {
        page(1);
    };
    // Changes the carousel's current index
    var changeIndex = function (index) {
        // This is set to bypass scroll event callbacks
        NAVIGATE_WITH_BTN.current = true;
        var newIndex;
        var maxIndex = itemCount - currentItemsVisible;
        if (index < 0) {
            newIndex = 0;
        }
        else if (index >= maxIndex) {
            newIndex = maxIndex;
        }
        else {
            newIndex = index;
        }
        setCurrentIndex(newIndex);
    };
    var setTokenStyles = function () {
        var wrapperElement = wrapperRef.current;
        if (wrapperElement) {
            peekWidth = parseInt(getComputedStyle(wrapperElement).getPropertyValue('--peek-width'), 10) * 16 || peekWidth;
            itemPadding =
                parseInt(getComputedStyle(wrapperElement).getPropertyValue('--item-padding'), 10) * 16 || itemPadding;
        }
    };
    var renderCarousel = function () {
        var _a, _b;
        var styleObj = {};
        styleObj['--carousel-item-count'] = itemsVisible[size];
        styleObj['--carousel-item-count-sm'] = itemsVisible.sm;
        styleObj['--carousel-item-count-md'] = itemsVisible.md;
        styleObj['--carousel-item-count-lg'] = itemsVisible.lg;
        styleObj['--carousel-item-count-xl'] = itemsVisible.xl ? itemsVisible.xl : itemsVisible.lg;
        var cssClassNames = getClassNameList('uitk-carousel', (!peek || !(currentItemsVisible < itemCount)) && 'no-peek', !pagingInset && 'no-inset', !isTouch && 'no-touch', itemsMaxHeight && 'items-max-height', className, (_a = {}, _a[size] = !!size && typeof window !== 'undefined', _a));
        var navProps = {
            analytics: analytics,
            buttonText: buttonText,
            carouselName: carouselName,
            currentIndex: currentIndex,
            itemCount: itemCount,
            itemsVisible: itemsVisible,
            fullSheetVisible: fullSheetVisible,
            loop: loop,
            navigationIconId: navigationIconId,
            onNextCb: onNextCb,
            onPrevCb: onPrevCb,
            pagingOverlay: pagingOverlay,
            size: size,
            isRtl: isRtl
        };
        var CarouselHeaderLevel = ((carouselHeaderLevel && UitkHeadingTags.get(carouselHeaderLevel)) ||
            UitkHeadingTags.get('h3'));
        return (React.createElement("div", { className: cssClassNames, ref: wrapperRef, "data-stid": "carousel-wrapper" },
            carouselHeaderText && (React.createElement(CarouselHeaderLevel, { className: getClassNameList((_b = {}, _b["is-visually-hidden"] = !carouselHeaderVisible, _b)) }, carouselHeaderText)),
            React.createElement("div", { className: getClassNameList('uitk-carousel-container'), "aria-label": carouselHeaderText, ref: containerRef, onScroll: onScroll, "data-stid": "carousel-container", style: styleObj, 
                // eslint-disable-next-line
                tabIndex: 0 },
                React.createElement(UitkCarouselItemList, { itemsVisible: itemsVisible, size: size, currentIndex: currentIndex, l10n: l10n, peek: peek, wrapItems: wrapItems, itemCount: itemCount, stretchToFill: stretchToFill, carouselListRef: carouselListRef }, children)),
            !hideNav && React.createElement(UitkCarouselNav, __assign({}, navProps))));
    };
    // Hooks
    React.useEffect(function () {
        mounted && announceCards();
        if (sizeInitialized)
            viewItemAnalytics();
        // Turn off free scroll and yet advance the carousel
        if (NAVIGATE_WITH_BTN.current) {
            NAVIGATE_WITH_BTN.current = false;
            AVOID_FREE_SCROLL.current = true;
            var effectivePeekWidth = peek && currentIndex !== 0 ? (peekWidth + itemPadding) / 2 / currentIndex : 0;
            scrollCarousel(currentIndex * (scrollDistance.current - effectivePeekWidth));
        }
        onIndexChange && onIndexChange(currentIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, sizeInitialized]);
    React.useEffect(function () {
        // Initialize viewport size
        setViewportSize();
        // Ensure that scrollLeft position is reset on mount
        if (!startIndex && containerRef.current) {
            containerRef.current.scrollLeft = 0;
        }
        // Change carousel's index to startIndex without triggering scroll animation
        if (startIndex) {
            SCROLL_WITHOUT_ANIMATION.current = true;
            changeIndex(startIndex);
        }
        else {
            setMounted(true);
        }
        // Initialize token values set from CSS
        setTokenStyles();
        'ontouchstart' in window && setIsTouch(true);
        document.querySelector('html').getAttribute('dir') === 'rtl' && setIsRtl(true);
        // Adds empty aria-live region to page to ensure Jaws screen reader
        // announces on initial key press. Relates to https://jira.expedia.biz/browse/WCAG-8258
        liveAnnounce('');
        if (sizeInitialized)
            viewItemAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizeInitialized]);
    React.useEffect(function () {
        var windowWidth = window.innerWidth;
        var debounceResizeHandler = debounce(INTERACTION_TIMER, function () {
            if (windowWidth === window.innerWidth) {
                return;
            }
            windowWidth = window.innerWidth;
            setViewportSize();
            var updatedCurrentIndexOnResize = getCurrentIndex();
            // Ensures that upon resize, the carousel will scroll to a position where the updated index slide will completely show.
            setCurrentIndex(updatedCurrentIndexOnResize);
            var effectivePeekWidth = peek && updatedCurrentIndexOnResize !== 0 ? (peekWidth + itemPadding) / 2 / updatedCurrentIndexOnResize : 0;
            scrollCarousel(updatedCurrentIndexOnResize * (scrollDistance.current - effectivePeekWidth));
        });
        window.addEventListener('resize', debounceResizeHandler);
        setScrollDistance();
        return function () { return window.removeEventListener('resize', debounceResizeHandler); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size, currentIndex]);
    React.useEffect(function () {
        onChange && onChange(containerRef);
        setTabIndex();
    });
    React.useImperativeHandle(carouselRef, function () { return ({
        changeIndex: function (index) { return changeIndex(index); }
    }); });
    // Carousel main functions -- Ends
    return renderCarousel();
};
var UitkCarousel = function (props) {
    var l10n = useLocalization();
    return React.createElement(Carousel, __assign({ l10n: l10n }, props));
};
UitkCarousel.displayName = 'UitkCarousel';
UitkCarousel.defaultProps = {
    hideNav: false,
    itemsMaxHeight: false,
    navigationIconId: {
        next: 'next-button',
        prev: 'prev-button'
    },
    loop: false,
    pagingInset: false,
    pagingOverlay: false,
    peek: false,
    shouldSnapOnScroll: true,
    startIndex: 0,
    stretchToFill: true,
    wrapItems: true
};

export { Carousel, UitkCarousel, UitkCarouselLocalAnalyticsIdType };
